import React, { useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import StackedCards from '../../../../Icon/icons/StackedCards/index'
import Users from '../../../../Icon/icons/Users/index'
import PlayPlaylist from '../../../../Icon/icons/PlayPlaylist/index'
import ShareAndroid from '../../../../Icon/icons/ShareAndroid/index'
import Button, { TYPES } from '../../../../Button/index'
import { H3, H4 } from '../../../../Heading/index'
import useCommunityImage from '../../../../../services/community/shared/useCommunityImage'
import { useCommunityModal } from '../../../../../services/community/shared/useCommunityModal'

export interface CommunityMyLibraryWelcomeProps {
  staticText: InternationalStrings
  onDismiss: (permanent: boolean) => void
  loggedInUuid?: string
  // Soft in-page navigation to the Discover tab. When provided, a plain
  // left-click on the "Discover" link runs this instead of a full reload.
  onDiscoverClick?: () => void
}

// Real, shareable target for the "Discover" link. The library page reads the
// `?tab=` query param on load (see useCollectionsData `initialTab`) so opening
// this URL directly — right-click → new tab, paste, etc. — lands on Discover.
const DISCOVER_TAB_HREF = '/playlist/my-library?tab=discover'

// ---------------------------------------------------------------------------
// FeatureRow — one of the three "plain" feature cards (icon + single label).
// Lives as its own component so the `useTranslatedText` hook is called once
// per row (rules of hooks; the parent renders multiple rows).
// ---------------------------------------------------------------------------

interface FeatureRowProps {
  itemKey: string
  Icon: React.ComponentType
}

function FeatureRow ({ itemKey, Icon }: FeatureRowProps) {
  const label = useTranslatedText({ keys: [itemKey] })
  return (
    <div className="community-my-library-welcome__option">
      <span className="community-my-library-welcome__option-icon">
        <Icon />
      </span>
      <span className="community-my-library-welcome__option-text">
        {label}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProfileFeatureRow — the "Gaia Profile" card. Same markup as FeatureRow but
// the copy is split into three parts so the middle "Gaia Profile" span renders
// as an inline link to the member's own profile. Raw <a> per the Community
// carve-out for profile links (SEO-crawlable, native open-in-new-tab).
// ---------------------------------------------------------------------------

interface ProfileFeatureRowProps {
  loggedInUuid?: string
}

function ProfileFeatureRow ({ loggedInUuid }: ProfileFeatureRowProps) {
  const part1 = useTranslatedText({ keys: ['featureProfilePagesPart1'] })
  const linkText = useTranslatedText({ keys: ['featureProfilePagesLink'] })
  const part2 = useTranslatedText({ keys: ['featureProfilePagesPart2'] })
  // Fall back to the create/edit page when the uuid is somehow absent — same
  // fallback ProfileMyPlaylists uses for its "view profile" target.
  const profileHref = loggedInUuid ? `/profile/${loggedInUuid}` : '/profile/edit'
  return (
    <div className="community-my-library-welcome__option">
      <span className="community-my-library-welcome__option-icon">
        <Users />
      </span>
      <span className="community-my-library-welcome__option-text">
        {part1}
        <a
          className="community-my-library-welcome__option-link"
          href={profileHref}
        >
          {linkText}
        </a>
        {part2}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// DiscoverFeatureRow — the "Discover playlists…" card. The leading "Discover"
// word is an inline link to the library's Discover tab. The anchor carries a
// real href (so open-in-new-tab / right-click work), but a plain left-click is
// intercepted to dismiss the modal and switch tabs in-page (no URL update) so
// the welcome modal does not reappear on reload.
// ---------------------------------------------------------------------------

interface DiscoverFeatureRowProps {
  onDiscoverClick?: () => void
}

function DiscoverFeatureRow ({ onDiscoverClick }: DiscoverFeatureRowProps) {
  const linkText = useTranslatedText({ keys: ['featureDiscoverPlaylistsLink'] })
  const rest = useTranslatedText({ keys: ['featureDiscoverPlaylistsRest'] })

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the browser handle modified / non-primary clicks natively
      // (open-in-new-tab, etc.) — the href lands on Discover via the page's
      // `?tab=` deep-link reading.
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
        || !onDiscoverClick
      ) {
        return
      }
      event.preventDefault()
      onDiscoverClick()
    },
    [onDiscoverClick],
  )

  return (
    <div className="community-my-library-welcome__option">
      <span className="community-my-library-welcome__option-icon">
        <PlayPlaylist />
      </span>
      <span className="community-my-library-welcome__option-text">
        <a
          className="community-my-library-welcome__option-link"
          href={DISCOVER_TAB_HREF}
          onClick={handleClick}
        >
          {linkText}
        </a>
        {rest}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// TitleContent — the split title with the italicized "new" span. Used by
// both the mobile (H4) and desktop (H3) heading variants so the markup stays
// identical except for the wrapping heading level.
// ---------------------------------------------------------------------------

function TitleContent () {
  const titlePart1Text = useTranslatedText({ keys: ['titlePart1'] })
  const titleEmphasisText = useTranslatedText({ keys: ['titleEmphasis'] })
  const titlePart2Text = useTranslatedText({ keys: ['titlePart2'] })
  return (
    <>
      {titlePart1Text}
      <span className="community-my-library-welcome__title-emphasis">
        {titleEmphasisText}
      </span>
      {titlePart2Text}
    </>
  )
}

// ---------------------------------------------------------------------------
// Inner — renders the portal once the static-text provider is in scope.
// ---------------------------------------------------------------------------

interface CommunityMyLibraryWelcomeContentProps {
  onDismiss: (permanent: boolean) => void
  loggedInUuid?: string
  onDiscoverClick?: () => void
}

function CommunityMyLibraryWelcomeContent ({
  onDismiss,
  loggedInUuid,
  onDiscoverClick,
}: CommunityMyLibraryWelcomeContentProps) {
  const [doNotShow, setDoNotShow] = useState(false)
  const heroSrc = useCommunityImage('community-my-library-welcome-hero.png')
  const tabletSrc = useCommunityImage('library-welcome-device-tablet.png')
  const phoneSrc = useCommunityImage('library-welcome-device-phone.png')

  const subtitleText = useTranslatedText({ keys: ['subtitle'] })
  const doNotShowAgainText = useTranslatedText({ keys: ['doNotShowAgain'] })
  const gotItText = useTranslatedText({ keys: ['gotIt'] })

  const dialogRef = useRef<HTMLDivElement>(null)

  // Escape always does a temporary dismiss; the "do not show again" choice is
  // only persisted by the explicit Got It click below.
  const handleClose = useCallback(() => {
    onDismiss(false)
  }, [onDismiss])

  // Unified scroll lock (body__modal--open), focus trap, Escape handler,
  // and focus restoration on unmount — same hook VibeQuizModal uses.
  useCommunityModal({ dialogRef, onClose: handleClose })

  const handleGotIt = () => {
    onDismiss(doNotShow)
  }

  const titleId = 'community-my-library-welcome-title'

  // Skip rendering during SSR — createPortal requires document.body.
  if (typeof document === 'undefined') {
    return null
  }

  const overlay = (
    <div className="community-my-library-welcome__overlay">
      <div
        ref={dialogRef}
        className="community-my-library-welcome__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {/*
          Hero region — marble bg image layered behind the heading content,
          plus two floating device mockups (phone in front, tablet behind)
          on the right side. See Figma nodes 3844:56868 (desktop) and
          3819:56317 (mobile) for layout specs.
        */}
        <div className="community-my-library-welcome__hero">
          {heroSrc && (
            <img
              className="community-my-library-welcome__hero-image"
              src={heroSrc}
              alt=""
              aria-hidden="true"
            />
          )}
          <div className="community-my-library-welcome__heading">
            <H4
              className="community-my-library-welcome__title community-my-library-welcome__title--mobile"
              id={titleId}
            >
              <TitleContent />
            </H4>
            <H3
              className="community-my-library-welcome__title community-my-library-welcome__title--desktop"
            >
              <TitleContent />
            </H3>
            <span className="community-my-library-welcome__subtitle">
              {subtitleText}
            </span>
          </div>
          {tabletSrc && (
            <img
              className="community-my-library-welcome__device community-my-library-welcome__device--tablet"
              src={tabletSrc}
              alt=""
              aria-hidden="true"
            />
          )}
          {phoneSrc && (
            <img
              className="community-my-library-welcome__device community-my-library-welcome__device--phone"
              src={phoneSrc}
              alt=""
              aria-hidden="true"
            />
          )}
        </div>
        <div className="community-my-library-welcome__body">
          <div className="community-my-library-welcome__options">
            <div className="community-my-library-welcome__option-column">
              <FeatureRow
                itemKey="featureNavigateLibrary"
                Icon={StackedCards}
              />
              <DiscoverFeatureRow onDiscoverClick={onDiscoverClick} />
            </div>
            <div className="community-my-library-welcome__option-column">
              <ProfileFeatureRow loggedInUuid={loggedInUuid} />
              <FeatureRow
                itemKey="featureShareChoices"
                Icon={ShareAndroid}
              />
            </div>
          </div>
          <div className="community-my-library-welcome__footer">
            <label className="community-my-library-welcome__checkbox">
              <input
                type="checkbox"
                className="community-my-library-welcome__checkbox-input"
                checked={doNotShow}
                onChange={(e) => setDoNotShow(e.target.checked)}
              />
              <span className="community-my-library-welcome__checkbox-label">
                {doNotShowAgainText}
              </span>
            </label>
            <Button
              type={TYPES.PRIMARY}
              className="community-my-library-welcome__button"
              onClick={handleGotIt}
            >
              {gotItText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(overlay, document.body)
}

// ---------------------------------------------------------------------------
// Outer — wraps the Inner in the StaticTextProvider so every descendant
// `useTranslatedText` resolves against the `communityMyLibraryWelcome` slice.
// ---------------------------------------------------------------------------

function CommunityMyLibraryWelcome ({
  staticText,
  onDismiss,
  loggedInUuid,
  onDiscoverClick,
}: CommunityMyLibraryWelcomeProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <CommunityMyLibraryWelcomeContent
        onDismiss={onDismiss}
        loggedInUuid={loggedInUuid}
        onDiscoverClick={onDiscoverClick}
      />
    </StaticTextProvider>
  )
}

export default connectStaticText({ storeKey: 'communityMyLibraryWelcome' })(
  CommunityMyLibraryWelcome,
)
